import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Plus, Search, Folder, ChevronDown, Filter } from 'lucide-react';
import { useBusiness } from '../contexts/BusinessContext';
import EmptyState from '../components/EmptyState';
import PermissionGuard from '../components/PermissionGuard';
import CreateProjectModal from '../components/CreateProjectModal';
import ProjectInfoModal from '../components/ProjectInfoModal';
import { supabase } from '../lib/supabase';
import toast from 'react-hot-toast';
import BookingCustomerInfo from '../components/booking/BookingCustomerInfo';
import CustomerDetailsModal from '../components/CustomerDetailsModal';
import { Customer, getCustomer } from '../lib/customers';
import { Proposal } from '../lib/proposals';
import { formatCurrency } from '../lib/utils';
import { FileText } from 'lucide-react';
import { getUnreadCountForQuoteRequestGroup } from '../lib/quoteRequests';
import { getUnreadCountForOrder } from '../lib/purchaseOrders';
import LostReasonModal from '../components/LostReasonModal';

interface Project {
  id: string;
  business_id: string;
  title: string;
  resource_id?: string | null;
  proposal_id?: string | null;
  booking_id?: string | null;
  pipeline_opportunities_id?: number | null;
  customer_id?: string | null;
  customer_source?: string | null;
  status: string;
  project_status?: string;
  created_at: string;
  updated_at: string;
}

const PROJECT_STATUSES = [
  'draft',
  'Quote - Expired',
  'Field Quote Scheduled',
  'Field Quote Completed',
  'Quote - Pending Approval',
  'Quote - Approved',
  'Measurements scheduled',
  'Measurements Complete',
  'Pending',
  'Converted to booking'
] as const;

// Color mapping for each status
const getStatusColor = (status: string): string => {
  const colorMap: Record<string, string> = {
    'draft': '#9CA3AF', // gray
    'Quote - Expired': '#EF4444', // red
    // Field quote scheduled: orange
    'Field Quote Scheduled': '#F97316',
    'Quote - Pending Approval': '#FBBF24', // yellow
    'Quote - Approved': '#10B981', // green
    // Measurement scheduled: purple/violet
    'Measurements scheduled': '#A855F7',
    'Measurements Complete': '#8B5CF6', // darker purple
    'Pending': '#F59E0B', // amber
    // Converted to booking: green
    'Converted to booking': '#22C55E',
  };
  return colorMap[status] || '#9CA3AF'; // default to gray
};

/**
 * Projects - Projects Addon App
 * 
 * Project management and tracking system for organizing work and tasks.
 * This is a new addon app that allows businesses to manage projects.
 */
export default function Projects() {
  const location = useLocation();
  const navigate = useNavigate();
  const { selectedBusiness } = useBusiness();
  const [searchQuery, setSearchQuery] = useState('');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [projects, setProjects] = useState<Project[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [isProjectInfoModalOpen, setIsProjectInfoModalOpen] = useState(false);
  const [customerMap, setCustomerMap] = useState<Map<string, Customer>>(new Map());
  const [proposalMap, setProposalMap] = useState<Map<string, Proposal>>(new Map());
  const [fieldQuoteMap, setFieldQuoteMap] = useState<Map<string, any[]>>(new Map());
  const [quoteRequestsMap, setQuoteRequestsMap] = useState<Map<string, any[]>>(new Map());
  const [ordersMap, setOrdersMap] = useState<Map<string, any[]>>(new Map());
  const [quoteUnreadCountsMap, setQuoteUnreadCountsMap] = useState<Map<string, number>>(new Map()); // Quote-specific
  const [orderUnreadCountsMap, setOrderUnreadCountsMap] = useState<Map<string, number>>(new Map()); // Order-specific
  const [projectTotalsMap, setProjectTotalsMap] = useState<Map<string, number>>(new Map()); // Map project_id -> total amount from services
  const [bookingNumbersMap, setBookingNumbersMap] = useState<Map<string, string>>(new Map()); // Map project_id -> booking_number
  const [openDropdownId, setOpenDropdownId] = useState<string | null>(null);
  const [updatingStatusId, setUpdatingStatusId] = useState<string | null>(null);
  const statusDropdownTappedRef = useRef(false);
  const isScrollingRef = useRef(false);
  const scrollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [projectStatusFilter, setProjectStatusFilter] = useState<string>('open');
  const [isFilterDropdownOpen, setIsFilterDropdownOpen] = useState(false);
  const [isCustomerDetailsModalOpen, setIsCustomerDetailsModalOpen] = useState(false);
  const [selectedCustomerForDetails, setSelectedCustomerForDetails] = useState<Customer | null>(null);

  // Lost reason modal state
  const [recordLostReasonEnabled, setRecordLostReasonEnabled] = useState(true);
  const [isLostReasonModalOpen, setIsLostReasonModalOpen] = useState(false);
  const [pendingCancelProject, setPendingCancelProject] = useState<Project | null>(null);

  // Keep selectedProject in sync when projects array updates
  useEffect(() => {
    if (selectedProject && projects.length > 0) {
      const updatedProject = projects.find(p => p.id === selectedProject.id);
      if (updatedProject && JSON.stringify(updatedProject) !== JSON.stringify(selectedProject)) {
        setSelectedProject(updatedProject);
      }
    }
  }, [projects, selectedProject]);

  // Fetch projects from database
  const fetchProjects = async () => {
    if (!selectedBusiness?.id) return;

    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('projects_active')
        .select('*')
        .eq('business_id', selectedBusiness.id)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching projects:', error);
        toast.error('Failed to load projects');
        return;
      }

      const projectsData = data || [];
      setProjects(projectsData);

      // Collect IDs for batch queries
      const customerIds = [...new Set(projectsData.map((p: Project) => p.customer_id).filter(Boolean))] as string[];
      const proposalIds = [...new Set(projectsData.map((p: Project) => p.proposal_id).filter(Boolean))] as string[];
      const projectIds = projectsData.map((p: Project) => p.id);
      const bookingIds = [...new Set(projectsData.map((p: Project) => p.booking_id).filter(Boolean))] as string[];

      // Fire ALL data fetches in parallel — single batch query each
      const [customersResult, proposalsResult, quoteRequestsResult, ordersResult, fieldQuotesResult, servicesResult, bookingsResult] = await Promise.all([
        // Batch customers
        customerIds.length > 0
          ? supabase.from('customers').select('*').in('id', customerIds)
          : Promise.resolve({ data: [], error: null }),

        // Batch proposals — only fields needed for card pills
        proposalIds.length > 0
          ? supabase.from('proposals').select('id, title, status, total_amount').in('id', proposalIds)
          : Promise.resolve({ data: [], error: null }),

        // Batch quote requests — single query instead of N+1
        projectIds.length > 0
          ? supabase.from('quote_requests').select('id, project_id, quote_request_group_id, status').eq('business_id', selectedBusiness.id).in('project_id', projectIds)
          : Promise.resolve({ data: [], error: null }),

        // Batch purchase orders — only fields needed for card pills, filtered by project
        projectIds.length > 0
          ? supabase.from('purchase_orders').select('id, project_id, po_number, status').eq('business_id', selectedBusiness.id).in('project_id', projectIds).neq('status', 'draft')
          : Promise.resolve({ data: [], error: null }),

        // Batch field quotes
        projectIds.length > 0
          ? supabase.from('project_field_quotes').select('id, project_id, status, is_measurement').eq('business_id', selectedBusiness.id).in('project_id', projectIds).order('created_at', { ascending: false })
          : Promise.resolve({ data: [], error: null }),

        // Batch project services — only totals
        projectIds.length > 0
          ? supabase.from('project_services').select('project_id, total_amount').in('project_id', projectIds)
          : Promise.resolve({ data: [], error: null }),

        // Batch booking numbers
        bookingIds.length > 0
          ? supabase.from('bookings').select('id, booking_number').in('id', bookingIds)
          : Promise.resolve({ data: [], error: null }),
      ]);

      // Process customers
      const newCustomerMap = new Map<string, Customer>();
      if (!customersResult.error && customersResult.data) {
        (customersResult.data as Customer[]).forEach(c => newCustomerMap.set(c.id, c));
      }
      setCustomerMap(newCustomerMap);

      // Process proposals
      const newProposalMap = new Map<string, Proposal>();
      if (!proposalsResult.error && proposalsResult.data) {
        proposalsResult.data.forEach((p: any) => newProposalMap.set(p.id, p as Proposal));
      }
      setProposalMap(newProposalMap);

      // Process quote requests — group by project_id
      const newQuoteRequestsMap = new Map<string, any[]>();
      if (!quoteRequestsResult.error && quoteRequestsResult.data) {
        quoteRequestsResult.data.forEach((qr: any) => {
          if (qr.project_id) {
            const existing = newQuoteRequestsMap.get(qr.project_id) || [];
            existing.push(qr);
            newQuoteRequestsMap.set(qr.project_id, existing);
          }
        });
      }
      setQuoteRequestsMap(newQuoteRequestsMap);

      // Process purchase orders — group by project_id, mapped to legacy pill shape
      const newOrdersMap = new Map<string, any[]>();
      if (!ordersResult.error && ordersResult.data) {
        ordersResult.data.forEach((po: any) => {
          if (po.project_id) {
            const existing = newOrdersMap.get(po.project_id) || [];
            existing.push({
              id: po.id,
              project_id: po.project_id,
              order_number: po.po_number,
              status:
                po.status === 'cancelled' ? 'cancelled'
                : po.status === 'received' || po.status === 'closed' ? 'received'
                : 'pending',
            });
            newOrdersMap.set(po.project_id, existing);
          }
        });
      }
      setOrdersMap(newOrdersMap);

      // Process field quotes — group by project_id, one per type
      const newFieldQuoteMap = new Map<string, any[]>();
      if (!fieldQuotesResult.error && fieldQuotesResult.data) {
        fieldQuotesResult.data.forEach((fq: any) => {
          const existing = newFieldQuoteMap.get(fq.project_id) || [];
          const hasQuote = existing.some((item: any) => !item.is_measurement);
          const hasMeasurement = existing.some((item: any) => item.is_measurement);
          if (fq.is_measurement && !hasMeasurement) {
            newFieldQuoteMap.set(fq.project_id, [...existing, fq]);
          } else if (!fq.is_measurement && !hasQuote) {
            newFieldQuoteMap.set(fq.project_id, [...existing, fq]);
          }
        });
      }
      setFieldQuoteMap(newFieldQuoteMap);

      // Process project services — calculate totals
      const newProjectTotalsMap = new Map<string, number>();
      if (!servicesResult.error && servicesResult.data) {
        servicesResult.data.forEach((service: any) => {
          if (service.project_id && service.total_amount) {
            const existing = newProjectTotalsMap.get(service.project_id) || 0;
            const total = existing + (Number(service.total_amount) || 0);
            if (total > 0) newProjectTotalsMap.set(service.project_id, total);
          }
        });
      }
      setProjectTotalsMap(newProjectTotalsMap);

      // Process booking numbers
      const newBookingNumbersMap = new Map<string, string>();
      if (!bookingsResult.error && bookingsResult.data) {
        const bookingIdToNumber = new Map<string, string>();
        bookingsResult.data.forEach((b: any) => {
          if (b.id && b.booking_number) bookingIdToNumber.set(b.id, b.booking_number);
        });
        projectsData.forEach((project: Project) => {
          if (project.booking_id && bookingIdToNumber.has(project.booking_id)) {
            newBookingNumbersMap.set(project.id, bookingIdToNumber.get(project.booking_id)!);
          }
        });
      }
      setBookingNumbersMap(newBookingNumbersMap);
    } catch (error) {
      console.error('Error fetching projects:', error);
      toast.error('Failed to load projects');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchProjects();
  }, [selectedBusiness?.id]);

  // Load CRM lost reason setting
  useEffect(() => {
    if (!selectedBusiness?.id) return;
    const loadLostReasonSetting = async () => {
      try {
        const { data } = await supabase.rpc('get_app_setting', {
          p_business_id: selectedBusiness.id,
          p_app_slug: 'crm',
          p_setting_key: 'record_lost_reason',
          p_default_value: true
        });
        if (data !== null) setRecordLostReasonEnabled(data);
      } catch (err) {
        console.error('Error loading lost reason setting:', err);
      }
    };
    loadLostReasonSetting();
  }, [selectedBusiness?.id]);

  // Handle navigation state to open a specific project
  // Use a ref to track if we've already opened from navigation state to prevent reopening
  const hasOpenedFromNavigation = useRef<string | null>(null);
  const pendingProjectId = useRef<string | null>(null);
  
  // Store the project ID from navigation state when it changes
  useEffect(() => {
    const state = location.state as { openProjectId?: string } | null;
    if (state?.openProjectId && !hasOpenedFromNavigation.current) {
      pendingProjectId.current = state.openProjectId;
      // Clear navigation state immediately to prevent loops
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, navigate, location.pathname]);
  
  // Handle opening the project when both navigation state and projects are ready
  useEffect(() => {
    // Only proceed if we have a pending project ID, projects are loaded, and modal is closed
    if (pendingProjectId.current && projects.length > 0 && !isProjectInfoModalOpen) {
      const projectId = pendingProjectId.current;
      
      // Find the project in our loaded projects
      const projectToOpen = projects.find(p => p.id === projectId);
      
      if (projectToOpen && hasOpenedFromNavigation.current !== projectId) {
        // Mark that we've opened this project from navigation
        hasOpenedFromNavigation.current = projectId;
        pendingProjectId.current = null; // Clear pending
        
        // Open the modal
        setSelectedProject(projectToOpen);
        setIsProjectInfoModalOpen(true);
      }
    }
  }, [projects, isProjectInfoModalOpen]); // Only depend on projects and modal state

  // Load unread badge counts lazily in background (after cards are displayed)
  useEffect(() => {
    const loadBadgeCounts = async () => {
      if (!selectedBusiness?.id) return;
      
      // Load quote unread counts
      if (quoteRequestsMap.size > 0) {
        const newQuoteUnreadCountsMap = new Map<string, number>();
        
        for (const [projectId, requests] of quoteRequestsMap) {
          const groupIds = [...new Set(
            requests
              .map((qr: any) => qr.quote_request_group_id)
              .filter((id: string | null): id is string => id !== null && id !== undefined)
          )];
          
          let quoteUnread = 0;
          for (const groupId of groupIds) {
            try {
              const count = await getUnreadCountForQuoteRequestGroup(selectedBusiness.id, groupId);
              quoteUnread += count;
            } catch (err) {
              // Silently ignore errors for background loading
            }
          }
          
          if (quoteUnread > 0) {
            newQuoteUnreadCountsMap.set(projectId, quoteUnread);
          }
        }
        
        setQuoteUnreadCountsMap(newQuoteUnreadCountsMap);
      }
      
      // Load order unread counts
      if (ordersMap.size > 0) {
        const newOrderUnreadCountsMap = new Map<string, number>();
        
        const orderUnreadPromises: Promise<{ projectId: string; count: number }>[] = [];
        for (const [projectId, orders] of ordersMap) {
          for (const order of orders) {
            if (order.id) {
              orderUnreadPromises.push(
                getUnreadCountForOrder(selectedBusiness.id, order.id)
                  .then(count => ({ projectId, count }))
                  .catch(() => ({ projectId, count: 0 }))
              );
            }
          }
        }
        
        const unreadResults = await Promise.all(orderUnreadPromises);
        
        for (const { projectId, count } of unreadResults) {
          const existing = newOrderUnreadCountsMap.get(projectId) || 0;
          newOrderUnreadCountsMap.set(projectId, existing + count);
        }
        
        setOrderUnreadCountsMap(newOrderUnreadCountsMap);
      }
    };
    
    // Small delay to ensure cards render first
    const timeoutId = setTimeout(loadBadgeCounts, 100);
    return () => clearTimeout(timeoutId);
  }, [selectedBusiness?.id, quoteRequestsMap, ordersMap]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (openDropdownId && !target.closest('[data-project-status-dropdown]')) {
        setOpenDropdownId(null);
      }
      if (isFilterDropdownOpen && !target.closest('[data-filter-dropdown]')) {
        setIsFilterDropdownOpen(false);
      }
    };

    if (openDropdownId || isFilterDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [openDropdownId, isFilterDropdownOpen]);

  // Filter projects by project_status first
  const filteredProjects = useMemo(() => {
    return projects.filter(project => {
      const status = project.project_status || 'open';
      return status === projectStatusFilter;
    });
  }, [projects, projectStatusFilter]);

  // Group filtered projects by workflow status
  const projectsByStatus = useMemo(() => {
    const grouped = new Map<string, Project[]>();
    PROJECT_STATUSES.forEach(status => {
      grouped.set(status, []);
    });
    
    filteredProjects.forEach(project => {
      const status = project.status || 'draft';
      if (grouped.has(status)) {
        grouped.get(status)!.push(project);
      } else {
        // If status doesn't exist in our list, add to draft
        grouped.get('draft')!.push(project);
      }
    });

    return grouped;
  }, [filteredProjects]);

  // Handle project status update
  const handleProjectStatusChange = async (projectId: string, newStatus: string) => {
    if (newStatus === 'Canceled' && recordLostReasonEnabled) {
      const project = projects.find(p => p.id === projectId);
      if (project?.pipeline_opportunities_id) {
        setPendingCancelProject(project);
        setIsLostReasonModalOpen(true);
        setOpenDropdownId(null);
        return;
      }
    }

    await executeProjectStatusChange(projectId, newStatus, null);
  };

  const executeProjectStatusChange = async (projectId: string, newStatus: string, lostReason: string | null) => {
    setUpdatingStatusId(projectId);
    try {
      const { error } = await supabase
        .from('projects')
        .update({ project_status: newStatus })
        .eq('id', projectId);

      if (error) {
        console.error('Error updating project status:', error);
        toast.error('Failed to update project status');
        return;
      }

      // If canceling a project linked to an opportunity, save the lost reason
      if (newStatus === 'Canceled') {
        const project = projects.find(p => p.id === projectId);
        if (project?.pipeline_opportunities_id) {
          await supabase
            .from('pipeline_opportunities')
            .update({ lost_reason: lostReason })
            .eq('id', project.pipeline_opportunities_id);
        }
      }

      setProjects(prevProjects =>
        prevProjects.map(p =>
          p.id === projectId ? { ...p, project_status: newStatus } : p
        )
      );

      toast.success('Project status updated');
      setOpenDropdownId(null);
    } catch (error) {
      console.error('Error updating project status:', error);
      toast.error('Failed to update project status');
    } finally {
      setUpdatingStatusId(null);
    }
  };

  const filteredProjectsByStatus = useMemo(() => {
    const filtered = new Map<string, Project[]>();
    
    projectsByStatus.forEach((projectsList, status) => {
      if (searchQuery.trim()) {
        const filteredList = projectsList.filter(project =>
          project.title.toLowerCase().includes(searchQuery.toLowerCase())
        );
        filtered.set(status, filteredList);
      } else {
        filtered.set(status, projectsList);
      }
    });

    return filtered;
  }, [projectsByStatus, searchQuery]);



  return (
    <PermissionGuard permission="projects.pageAccess" pageId="projects">
      {/* Stretch to full viewport width/height without outer gray gutters */}
      {/* Account for top navigation bar height (h-12 = 3rem = 48px + border) */}
      <div 
        className="min-h-screen bg-gray-50/30 -m-4 md:-m-6 flex flex-col overflow-hidden" 
        style={{ 
          height: 'calc(100vh - 3rem)', 
          maxHeight: 'calc(100vh - 3rem)',
          boxSizing: 'border-box',
          border: 'none',
          outline: 'none'
        }} 
        data-projects-page
      >
        {/* Header - iOS Style */}
        <div className="w-full px-4 sm:px-6 py-3 sm:py-4 flex-shrink-0" style={{ boxSizing: 'border-box', border: 'none', outline: 'none' }}>
          <div className="flex items-center gap-3">
            <div className="flex-shrink-0 hidden sm:block">
              <h1 className="text-xl sm:text-3xl font-bold text-gray-900 tracking-tight" style={{ fontWeight: 700, letterSpacing: '-0.02em' }}>Projects</h1>
            </div>
            <div className="relative flex-1 min-w-0">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search projects..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-3 py-2.5 text-sm border border-gray-200/60 rounded-xl bg-white/80 backdrop-blur-sm focus:outline-none focus:ring-2 focus:ring-blue-400/50 focus:border-blue-400/50 font-medium text-gray-700 shadow-sm transition-all duration-200"
                style={{ boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)' }}
              />
            </div>
            
            <div className="flex items-center gap-2 flex-shrink-0">
              <button
                onClick={() => setIsCreateModalOpen(true)}
                className="inline-flex items-center gap-1.5 px-3 py-2.5 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-semibold transition-all duration-200 active:scale-95 shadow-lg touch-manipulation"
                style={{ boxShadow: '0 2px 8px rgba(37, 99, 235, 0.3)', WebkitTapHighlightColor: 'transparent' }}
              >
                <Plus className="h-4 w-4" />
                <span>New</span>
              </button>
              
              {/* Project Status Filter */}
              <div className="relative" data-filter-dropdown>
                <button
                  onClick={() => setIsFilterDropdownOpen(!isFilterDropdownOpen)}
                  className="inline-flex items-center gap-1.5 px-3 py-2.5 text-sm border border-gray-200/60 rounded-xl bg-white/80 backdrop-blur-sm hover:bg-white font-medium text-gray-700 shadow-sm transition-all duration-200 active:scale-95 touch-manipulation"
                  style={{ boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)', WebkitTapHighlightColor: 'transparent' }}
                >
                  <Filter className="h-4 w-4 text-gray-400" />
                  <span className="text-xs text-gray-700 capitalize">{projectStatusFilter}</span>
                  <ChevronDown className={`h-3 w-3 text-gray-400 transition-transform ${isFilterDropdownOpen ? 'rotate-180' : ''}`} />
                </button>
                
                {/* Filter Dropdown */}
                {isFilterDropdownOpen && (
                  <div 
                    className="absolute right-0 top-full mt-1.5 bg-white rounded-xl shadow-lg border border-gray-200/80 z-50 min-w-[140px] overflow-hidden"
                    style={{ boxShadow: '0 8px 30px rgba(0,0,0,0.12), 0 2px 8px rgba(0,0,0,0.06)' }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    {['open', 'Completed', 'Canceled'].map((status) => (
                      <button
                        key={status}
                        onClick={() => {
                          setProjectStatusFilter(status);
                          setIsFilterDropdownOpen(false);
                        }}
                        className={`w-full text-left px-3.5 py-2.5 text-sm transition-colors duration-150 touch-manipulation ${
                          projectStatusFilter === status
                            ? 'bg-blue-50 text-blue-700 font-semibold'
                            : 'hover:bg-gray-50 active:bg-gray-100 text-gray-700 font-medium'
                        }`}
                        style={{ minHeight: '44px', WebkitTapHighlightColor: 'transparent' }}
                      >
                        {status}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Main Content - Full Height Container (use viewport height; inner content scrolls, page does not) */}
        <div
          className="px-4 sm:px-6 lg:px-8 flex-1 overflow-hidden min-h-0"
          style={{ maxHeight: '100%', height: '100%', overflow: 'hidden' }}
        >
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-gray-500">Loading projects...</div>
            </div>
          ) : projects.length === 0 ? (
            <EmptyState
              icon={<Folder className="h-12 w-12 text-gray-400" />}
              title="No projects yet"
              description="Get started by creating your first project to organize and track your work."
              actionLabel="Create Project"
              onAction={() => setIsCreateModalOpen(true)}
            />
          ) : (
            <>
            {/* Kanban Board - Full Height with Horizontal Scroll */}
            <div 
              className="flex space-x-4 sm:space-x-6 overflow-x-auto ios-momentum-scroll" 
              style={{
                scrollbarWidth: 'thin',
                scrollbarColor: 'rgba(156, 163, 175, 0.2) transparent',
                scrollSnapType: 'x mandatory',
                WebkitOverflowScrolling: 'touch',
                height: 'calc(100% - 0px)',
                maxHeight: 'calc(100% - 0px)',
                overflowY: 'hidden',
                overflowX: 'auto',
              }}
            >
              {PROJECT_STATUSES.filter(status => {
                // Only show status columns that have projects
                const projectsInStatus = projectsByStatus.get(status) || [];
                return projectsInStatus.length > 0;
              }).map(status => {
                const statusProjects = filteredProjectsByStatus.get(status) || [];
                
                return (
                  <div 
                    key={status} 
                    className="flex-shrink-0 w-80 bg-gray-50/50 backdrop-blur-sm border border-gray-200/60 rounded-2xl p-4 transition-all duration-200 flex flex-col"
                    style={{
                      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
                      scrollSnapAlign: 'start',
                      scrollSnapStop: 'always',
                      height: '100%',
                      maxHeight: '100%',
                    }}
                  >
                    {/* Column Header */}
                    <div className="flex items-center justify-between mb-3 flex-shrink-0">
                      <div>
                        <h3 className="font-semibold text-gray-900 text-sm tracking-tight capitalize">
                          {status}
                        </h3>
                        <div className="text-[11px] text-gray-500 mt-0.5 font-medium">
                          {statusProjects.length} {statusProjects.length === 1 ? 'project' : 'projects'}
                        </div>
                      </div>
                    </div>
                    
                    {/* Project Cards - Scrollable Container */}
                    <div 
                      className="space-y-3 flex-1 overflow-y-auto overflow-x-hidden ios-momentum-scroll"
                      style={{
                        scrollbarWidth: 'thin',
                        scrollbarColor: 'rgba(156, 163, 175, 0.3) transparent',
                        minHeight: 0,
                        maxHeight: '100%',
                        WebkitOverflowScrolling: 'touch',
                      }}
                      onScroll={() => {
                        isScrollingRef.current = true;
                        if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
                        scrollTimeoutRef.current = setTimeout(() => { isScrollingRef.current = false; }, 150);
                      }}
                    >
                      {statusProjects.length > 0 ? (
                        statusProjects.map((project: Project) => (
                          <div 
                            key={project.id} 
                            className="cursor-pointer"
                            style={{
                              WebkitTouchCallout: 'none',
                              WebkitTapHighlightColor: 'transparent',
                            }}
                            onClick={() => {
                              if (isScrollingRef.current) return;
                              if (statusDropdownTappedRef.current) {
                                statusDropdownTappedRef.current = false;
                                return;
                              }
                              if (openDropdownId !== project.id) {
                                setOpenDropdownId(null);
                              }
                              setSelectedProject(project);
                              setIsProjectInfoModalOpen(true);
                            }}
                          >
                            {/* Card Design - iOS Style matching CRM */}
                            <div
                              className="bg-white border border-gray-200/60 rounded-xl p-4 transition-all duration-200 group relative hover:shadow-lg hover:scale-[1.02] active:scale-[0.98]"
                              style={{
                                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)',
                                overflow: 'visible',
                              }}
                            >
                              <div className="flex flex-col">
                                  {/* Project Title */}
                                  <div className="mb-3 flex-shrink-0">
                                    <h4 className="font-semibold text-gray-900 leading-tight line-clamp-2 text-sm" style={{ letterSpacing: '-0.01em', fontWeight: 600 }}>
                                      {project.title || 'Untitled Project'}
                                    </h4>
                                    {(() => {
                                      const projectTotal = projectTotalsMap.get(project.id);
                                      const bookingNumber = bookingNumbersMap.get(project.id);
                                      if (projectTotal !== undefined && projectTotal > 0) {
                                        return (
                                          <div className="text-xs font-semibold text-gray-500 mt-1">
                                            {bookingNumber ? `Booking #${bookingNumber} · ` : ''}{formatCurrency(projectTotal)}
                                          </div>
                                        );
                                      }
                                      return null;
                                    })()}
                                  </div>

                                  {/* Customer Card - using shared BookingCustomerInfo */}
                                  {(() => {
                                    if (!project.customer_id) return null;

                                    const customer = customerMap.get(project.customer_id);
                                    if (!customer) {
                                      return (
                                        <div className="pt-3 border-t border-gray-100/80">
                                          <div className="flex items-center gap-2.5">
                                            <div className="w-7 h-7 bg-gradient-to-br from-blue-400 to-purple-400 rounded-full flex items-center justify-center shadow-sm">
                                              <span className="text-xs font-semibold text-white">
                                                {project.title?.charAt(0).toUpperCase() || 'P'}
                                              </span>
                                            </div>
                                            <span className="text-sm text-gray-700 font-medium">Loading...</span>
                                          </div>
                                        </div>
                                      );
                                    }

                                    return (
                                      <div
                                        className="pt-3 border-t border-gray-100/80"
                                        onClick={(e) => e.stopPropagation()}
                                        onMouseDown={(e) => e.stopPropagation()}
                                        onTouchStart={(e) => e.stopPropagation()}
                                      >
                                        <BookingCustomerInfo
                                          customer={customer}
                                          canEdit={true}
                                          onEdit={() => {
                                            setSelectedCustomerForDetails(customer);
                                            setIsCustomerDetailsModalOpen(true);
                                          }}
                                          mobile_booking={false}
                                          businessId={selectedBusiness?.id}
                                        />
                                      </div>
                                    );
                                  })()}

                                  {/* Proposal Display - iOS pill style */}
                                  {project.proposal_id && (() => {
                                    const proposal = proposalMap.get(project.proposal_id);
                                    if (!proposal) return null;
                                    return (
                                      <div className="mt-2">
                                        <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-blue-50 border border-blue-100/60">
                                          <FileText className="h-3 w-3 text-blue-500 flex-shrink-0" />
                                          <span className="text-[11px] font-semibold text-blue-700 truncate max-w-[120px]">
                                            {proposal.title || 'Proposal'}
                                          </span>
                                          {proposal.status && (
                                            <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full capitalize ${
                                              proposal.status === 'approved' ? 'bg-emerald-100 text-emerald-700' :
                                              proposal.status === 'sent' ? 'bg-blue-100 text-blue-700' :
                                              proposal.status === 'viewed' ? 'bg-purple-100 text-purple-700' :
                                              proposal.status === 'declined' ? 'bg-red-100 text-red-700' :
                                              'bg-gray-100 text-gray-600'
                                            }`}>
                                              {proposal.status}
                                            </span>
                                          )}
                                        </div>
                                      </div>
                                    );
                                  })()}

                                  {/* Supplies Quotes Badge */}
                                  {(() => {
                                    const quoteRequests = quoteRequestsMap.get(project.id);
                                    if (!quoteRequests || quoteRequests.length === 0) return null;
                                    
                                    const uniqueGroups = new Set(
                                      quoteRequests
                                        .map(qr => qr.quote_request_group_id)
                                        .filter((id): id is string => id !== null && id !== undefined)
                                    );
                                    const quoteCount = uniqueGroups.size;
                                    const unreadCount = quoteUnreadCountsMap.get(project.id) ?? 0;
                                    
                                    if (quoteCount === 0) return null;
                                    
                                    return (
                                      <div className="mt-2">
                                        <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-orange-50 border border-orange-100/60">
                                          <span className="text-[11px] font-semibold text-orange-700 truncate">
                                            Quotes ({quoteCount})
                                          </span>
                                          {unreadCount > 0 && (
                                            <span className="bg-red-500 text-white text-[9px] font-bold rounded-full min-w-[16px] h-[16px] flex items-center justify-center px-1 flex-shrink-0">
                                              {unreadCount > 99 ? '99+' : unreadCount}
                                            </span>
                                          )}
                                        </div>
                                      </div>
                                    );
                                  })()}

                                  {/* Supplies Ordered Badge */}
                                  {(() => {
                                    const orders = ordersMap.get(project.id);
                                    if (!orders || orders.length === 0) return null;
                                    
                                    const orderUnreadCount = orderUnreadCountsMap.get(project.id) ?? 0;
                                    
                                    return (
                                      <div className="mt-2 flex flex-wrap gap-1.5">
                                        {orders.map((order, index) => {
                                          if (!order || !order.order_number) return null;
                                          
                                          return (
                                            <div key={order.id || index} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-50 border border-emerald-100/60">
                                              <span className="text-[11px] font-semibold text-emerald-700 truncate">
                                                {order.order_number}
                                              </span>
                                              {order.status && (
                                                <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full capitalize ${
                                                  order.status?.toLowerCase() === 'received' ? 'bg-emerald-100 text-emerald-700' :
                                                  order.status?.toLowerCase() === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                                                  order.status?.toLowerCase() === 'cancelled' ? 'bg-red-100 text-red-700' :
                                                  'bg-gray-100 text-gray-600'
                                                }`}>
                                                  {order.status}
                                                </span>
                                              )}
                                              {index === 0 && orderUnreadCount > 0 && (
                                                <span className="bg-red-500 text-white text-[9px] font-bold rounded-full min-w-[16px] h-[16px] flex items-center justify-center px-1 flex-shrink-0">
                                                  {orderUnreadCount > 99 ? '99+' : orderUnreadCount}
                                                </span>
                                              )}
                                            </div>
                                          );
                                        })}
                                      </div>
                                    );
                                  })()}

                                  {/* Field Quote/Measurement Display */}
                                  {(() => {
                                    const fieldQuotes = fieldQuoteMap.get(project.id);
                                    if (!fieldQuotes || fieldQuotes.length === 0) return null;

                                    return (
                                      <div className="mt-2 flex flex-wrap gap-1.5">
                                        {fieldQuotes.map((fieldQuote: any) => {
                                          const isMeasurement = !!fieldQuote.is_measurement;
                                          const status = (fieldQuote.status || 'scheduled').toLowerCase();

                                          return (
                                            <div key={fieldQuote.id} className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border ${
                                              isMeasurement ? 'bg-purple-50 border-purple-100/60' : 'bg-orange-50 border-orange-100/60'
                                            }`}>
                                              <span className={`text-[11px] font-semibold truncate ${
                                                isMeasurement ? 'text-purple-700' : 'text-orange-700'
                                              }`}>
                                                {isMeasurement ? 'Measurement' : 'Field Quote'}
                                              </span>
                                              <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full capitalize ${
                                                status === 'completed' ? 'bg-emerald-100 text-emerald-700' :
                                                status === 'in progress' ? 'bg-yellow-100 text-yellow-700' :
                                                isMeasurement ? 'bg-purple-100 text-purple-700' : 'bg-orange-100 text-orange-700'
                                              }`}>
                                                {status}
                                              </span>
                                            </div>
                                          );
                                        })}
                                      </div>
                                    );
                                  })()}

                                  {/* Project Meta - Date and Status */}
                                  <div className="mt-auto pt-3 border-t border-gray-100/80 flex items-center justify-between">
                                    <div className="text-[11px] text-gray-400 font-medium">
                                      {new Date(project.created_at).toLocaleDateString('en-US', {
                                        month: 'short',
                                        day: 'numeric'
                                      })}
                                    </div>
                                    
                                    {/* Project Status Dropdown - iOS pill style matching CRM */}
                                    <div className="relative" data-project-status-dropdown>
                                      <button
                                        onPointerDown={(e) => {
                                          e.stopPropagation();
                                          statusDropdownTappedRef.current = true;
                                        }}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          e.preventDefault();
                                          statusDropdownTappedRef.current = true;
                                          setOpenDropdownId(openDropdownId === project.id ? null : project.id);
                                          setTimeout(() => { statusDropdownTappedRef.current = false; }, 300);
                                        }}
                                        disabled={updatingStatusId === project.id}
                                        className={`flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1 rounded-full transition-all duration-200 active:scale-95 disabled:opacity-50 touch-manipulation ${
                                          project.project_status === 'Completed' ? 'bg-emerald-100 text-emerald-700' :
                                          project.project_status === 'Canceled' ? 'bg-red-100 text-red-700' :
                                          'bg-blue-100 text-blue-700'
                                        }`}
                                        style={{ WebkitTapHighlightColor: 'transparent' }}
                                      >
                                        <span className={`w-1.5 h-1.5 rounded-full ${
                                          project.project_status === 'Completed' ? 'bg-emerald-500' :
                                          project.project_status === 'Canceled' ? 'bg-red-500' :
                                          'bg-blue-500'
                                        }`} />
                                        <span>{project.project_status || 'open'}</span>
                                        <ChevronDown className={`h-3 w-3 transition-transform ${openDropdownId === project.id ? 'rotate-180' : ''}`} />
                                      </button>
                                      
                                      {/* Dropdown Menu */}
                                      {openDropdownId === project.id && (
                                        <div 
                                          className="absolute bottom-full right-0 mb-1.5 bg-white rounded-xl shadow-lg border border-gray-200/80 z-50 min-w-[140px] overflow-hidden"
                                          style={{ boxShadow: '0 8px 30px rgba(0,0,0,0.12), 0 2px 8px rgba(0,0,0,0.06)' }}
                                          onClick={(e) => e.stopPropagation()}
                                          onPointerDown={(e) => { e.stopPropagation(); statusDropdownTappedRef.current = true; }}
                                        >
                                          {['open', 'Completed', 'Canceled'].map((status) => (
                                            <button
                                              key={status}
                                              onClick={(e) => { e.stopPropagation(); handleProjectStatusChange(project.id, status); }}
                                              className={`w-full flex items-center gap-2.5 px-3.5 py-2.5 text-xs font-medium transition-colors duration-150 touch-manipulation ${
                                                project.project_status === status
                                                  ? 'bg-gray-50'
                                                  : 'hover:bg-gray-50 active:bg-gray-100'
                                              }`}
                                              style={{ minHeight: '44px', WebkitTapHighlightColor: 'transparent' }}
                                            >
                                              <span className={`w-2 h-2 rounded-full ${
                                                status === 'Completed' ? 'bg-emerald-500' :
                                                status === 'Canceled' ? 'bg-red-500' :
                                                'bg-blue-500'
                                              }`} />
                                              <span className={`${
                                                status === 'Completed' ? 'text-emerald-700' :
                                                status === 'Canceled' ? 'text-red-700' :
                                                'text-blue-700'
                                              }`}>{status}</span>
                                            </button>
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </div>
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="text-center py-12 text-gray-400 text-sm">
                          <div className="opacity-50">No projects in this status</div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            </>
          )}
        </div>
      </div>

      {/* Create Project Modal */}
      <CreateProjectModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSuccess={(projectId) => {
          fetchProjects(); // Refresh project list
          setIsCreateModalOpen(false);
          if (projectId) {
            navigate(location.pathname, { state: { openProjectId: projectId } });
          }
        }}
      />

      {/* Project Info Modal */}
      <ProjectInfoModal
        key={selectedProject?.id || 'no-project'}
        isOpen={isProjectInfoModalOpen}
        onClose={() => {
          setIsProjectInfoModalOpen(false);
          setSelectedProject(null);
          // Clear the navigation tracking refs when modal is closed
          hasOpenedFromNavigation.current = null;
          pendingProjectId.current = null;
          // Refresh projects to update totals when services are added/removed
          fetchProjects();
        }}
        project={selectedProject}
        onUpdate={() => {
          fetchProjects(); // Refresh project list
        }}
        navigate={navigate}
      />
      {/* Customer Details Modal */}
      <CustomerDetailsModal
        isOpen={isCustomerDetailsModalOpen}
        onClose={() => setIsCustomerDetailsModalOpen(false)}
        customer={selectedCustomerForDetails}
        onUpdate={async () => {
          if (selectedCustomerForDetails?.id) {
            try {
              const refreshed = await getCustomer(selectedCustomerForDetails.id);
              setSelectedCustomerForDetails(refreshed);
              setCustomerMap(prev => new Map(prev).set(refreshed.id, refreshed));
            } catch (err) {
              console.error('Error refreshing customer after vehicle update:', err);
            }
          }
        }}
      />

      {/* Lost Reason Modal (when canceling a project linked to an opportunity) */}
      <LostReasonModal
        isOpen={isLostReasonModalOpen}
        businessId={selectedBusiness?.id || ''}
        onConfirm={(reason) => {
          setIsLostReasonModalOpen(false);
          if (pendingCancelProject) {
            executeProjectStatusChange(pendingCancelProject.id, 'Canceled', reason);
          }
          setPendingCancelProject(null);
        }}
        onSkip={() => {
          setIsLostReasonModalOpen(false);
          if (pendingCancelProject) {
            executeProjectStatusChange(pendingCancelProject.id, 'Canceled', null);
          }
          setPendingCancelProject(null);
        }}
        onCancel={() => {
          setIsLostReasonModalOpen(false);
          setPendingCancelProject(null);
        }}
      />
    </PermissionGuard>
  );
}
